/* Admin: Settings — donation instructions, contact info, site meta. Writes siteSettings/main. */
import { useEffect, useRef, useState } from "react";
import AdminShell from "../../components/shell/AdminShell.jsx";
import { Button, Field, Input, Textarea, Select, Card, Skeleton, Icon, MediaUploaderField, showToast } from "../../ui/index.js";
import { useSiteSettings } from "../../data/hooks.js";
import { saveSettings } from "../../data/mutations.js";

const DEFAULTS = {
  brand: "Purple Angels Kids Foundation",
  donation: { bankName: "", bankBranch: "", bankAccountName: "", bankAccountNumber: "", swift: "", momoProvider: "MTN Mobile Money", momoNumber: "", momoName: "", notes: "" },
  contact: { publicEmail: "", publicPhone: "", whatsappNumber: "", instagram: "", facebook: "", address: "" },
  meta: { siteName: "Purple Angels Kids Foundation", ogImage: null, gaId: "", privacyLastUpdated: "" },
  founder: { name: "Naa Dzama Sowah", role: "Co-Founder" },
};

export default function AdminSettingsPage() {
  const { settings: remote, loading } = useSiteSettings();
  const [tab, setTab] = useState("donation");
  const [settings, setSettings] = useState(DEFAULTS);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const seeded = useRef(false);

  useEffect(() => {
    if (!loading && !seeded.current) {
      setSettings({
        ...DEFAULTS, ...remote,
        donation: { ...DEFAULTS.donation, ...(remote?.donation || {}) },
        contact: { ...DEFAULTS.contact, ...(remote?.contact || {}) },
        meta: { ...DEFAULTS.meta, ...(remote?.meta || {}) },
        founder: { ...DEFAULTS.founder, ...(remote?.founder || {}) },
      });
      seeded.current = true;
    }
  }, [loading, remote]);

  const updateDonation = (k, v) => { setSettings((s) => ({ ...s, donation: { ...s.donation, [k]: v } })); setDirty(true); };
  const updateContact = (k, v) => { setSettings((s) => ({ ...s, contact: { ...s.contact, [k]: v } })); setDirty(true); };
  const updateMeta = (k, v) => { setSettings((s) => ({ ...s, meta: { ...s.meta, [k]: v } })); setDirty(true); };

  const save = async () => {
    setSaving(true);
    try {
      await saveSettings({ brand: settings.brand, donation: settings.donation, contact: settings.contact, meta: settings.meta, founder: settings.founder });
      setDirty(false);
      showToast({ intent: "success", title: "Saved" });
    } catch (err) {
      showToast({ intent: "danger", title: "Couldn't save", body: err.message });
    } finally {
      setSaving(false);
    }
  };

  if (loading && !seeded.current) return <AdminShell><Skeleton h={400} /></AdminShell>;
  const d = settings.donation, c = settings.contact, m = settings.meta;

  return (
    <AdminShell>
      <div className="admin-main__header">
        <div><h1 className="t-3xl">Settings</h1><p>Site-wide values used across the public site.</p></div>
      </div>

      <div className="tabs">
        <button className={`tab ${tab === "donation" ? "active" : ""}`} onClick={() => setTab("donation")}>Donation details</button>
        <button className={`tab ${tab === "contact" ? "active" : ""}`} onClick={() => setTab("contact")}>Contact info</button>
        <button className={`tab ${tab === "meta" ? "active" : ""}`} onClick={() => setTab("meta")}>Site meta</button>
      </div>

      <div style={{ marginTop: 32 }}>
        {tab === "donation" && (
          <Card style={{ maxWidth: 720 }}>
            <h2 className="t-2xl" style={{ marginTop: 0 }}>Donation details</h2>
            <div style={{ background: "var(--accent-yellow-soft)", color: "var(--warning)", padding: 12, borderRadius: "var(--radius-md)", marginTop: 12, fontSize: 13, display: "flex", gap: 8, alignItems: "center" }}>
              <Icon.Info size={16} />These details show on the public Donate page and on events. Keep them current.
            </div>
            <h3 className="t-base" style={{ marginTop: 32, marginBottom: 16, fontWeight: 600 }}>Bank transfer</h3>
            <div className="stack-lg">
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                <Field label="Bank name"><Input value={d.bankName} onChange={(e) => updateDonation("bankName", e.target.value)} /></Field>
                <Field label="Bank branch"><Input value={d.bankBranch} onChange={(e) => updateDonation("bankBranch", e.target.value)} /></Field>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                <Field label="Account name"><Input value={d.bankAccountName} onChange={(e) => updateDonation("bankAccountName", e.target.value)} /></Field>
                <Field label="Account number"><Input value={d.bankAccountNumber} onChange={(e) => updateDonation("bankAccountNumber", e.target.value)} /></Field>
              </div>
              <Field label="SWIFT code"><Input value={d.swift} onChange={(e) => updateDonation("swift", e.target.value)} /></Field>
            </div>
            <h3 className="t-base" style={{ marginTop: 32, marginBottom: 16, fontWeight: 600 }}>Mobile money</h3>
            <div className="stack-lg">
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                <Field label="Provider"><Select value={d.momoProvider} onChange={(e) => updateDonation("momoProvider", e.target.value)}><option>MTN Mobile Money</option><option>Telecel Cash</option><option>AirtelTigo Money</option></Select></Field>
                <Field label="Number"><Input value={d.momoNumber} onChange={(e) => updateDonation("momoNumber", e.target.value)} /></Field>
              </div>
              <Field label="Name on account"><Input value={d.momoName} onChange={(e) => updateDonation("momoName", e.target.value)} /></Field>
            </div>
            <div style={{ marginTop: 24 }}>
              <Field label="Additional instructions" helper="Optional note shown under the transfer details."><Textarea value={d.notes} onChange={(e) => updateDonation("notes", e.target.value)} rows={3} /></Field>
            </div>
            <div className="row" style={{ marginTop: 32, justifyContent: "flex-end" }}><Button variant="primary" loading={saving} onClick={save} disabled={!dirty}>Save</Button></div>
          </Card>
        )}

        {tab === "contact" && (
          <Card style={{ maxWidth: 720 }}>
            <h2 className="t-2xl" style={{ marginTop: 0 }}>Public contact details</h2>
            <p className="t-sm muted" style={{ marginTop: 4 }}>Shown on the Contact page and in the footer.</p>
            <div className="stack-lg" style={{ marginTop: 24 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                <Field label="Public email"><Input value={c.publicEmail} onChange={(e) => updateContact("publicEmail", e.target.value)} /></Field>
                <Field label="Public phone"><Input value={c.publicPhone} onChange={(e) => updateContact("publicPhone", e.target.value)} /></Field>
              </div>
              <Field label="WhatsApp number"><Input value={c.whatsappNumber} onChange={(e) => updateContact("whatsappNumber", e.target.value)} /></Field>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                <Field label="Instagram URL"><Input value={c.instagram} onChange={(e) => updateContact("instagram", e.target.value)} /></Field>
                <Field label="Facebook URL"><Input value={c.facebook} onChange={(e) => updateContact("facebook", e.target.value)} /></Field>
              </div>
              <Field label="Address"><Textarea value={c.address} onChange={(e) => updateContact("address", e.target.value)} rows={2} /></Field>
            </div>
            <div className="row" style={{ marginTop: 32, justifyContent: "flex-end" }}><Button variant="primary" loading={saving} onClick={save} disabled={!dirty}>Save</Button></div>
          </Card>
        )}

        {tab === "meta" && (
          <Card style={{ maxWidth: 720 }}>
            <h2 className="t-2xl" style={{ marginTop: 0 }}>Site metadata</h2>
            <div className="stack-lg" style={{ marginTop: 24 }}>
              <Field label="Brand / site name" helper="Shown in the nav, footer, and browser tab."><Input value={settings.brand} onChange={(e) => { setSettings((s) => ({ ...s, brand: e.target.value })); setDirty(true); }} /></Field>
              <Field label="Default social preview image" helper="1200×630 image used when pages are shared.">
                <MediaUploaderField value={m.ogImage} onChange={(v) => updateMeta("ogImage", v)} path="site/og" aspect="1200/630" />
              </Field>
              <Field label="Google Analytics ID" helper="Leave blank to disable analytics."><Input value={m.gaId} onChange={(e) => updateMeta("gaId", e.target.value)} /></Field>
              <Field label="Privacy notice last updated"><Input value={m.privacyLastUpdated} onChange={(e) => updateMeta("privacyLastUpdated", e.target.value)} placeholder="2026-06-16" /></Field>
            </div>
            <div className="row" style={{ marginTop: 32, justifyContent: "flex-end" }}><Button variant="primary" loading={saving} onClick={save} disabled={!dirty}>Save</Button></div>
          </Card>
        )}
      </div>
    </AdminShell>
  );
}
